import React, { ComponentType } from 'react'
import { Helmet } from 'react-helmet'

/**
 * Image used as a preview when the site is shared (WhatsApp, Messenger, Facebook,
 * LinkedIn, Twitter/X, Slack..). It lives in `static/` so its URL is stable and
 * does not change with every build (unlike images processed by sharp).
 *
 * Crawlers require an ABSOLUTE url, so it always has to be prefixed with the site url.
 */
export const shareImage = {
    path: '/og-image.png',
    width: 1200,
    height: 630,
    type: 'image/png'
}

type MetaProps = {
    title?: string
    description?: string
    image?: string
    imageWidth?: number
    imageHeight?: number
    imageType?: string
    url?: string
}

/**
 * Tags with an empty/undefined value are not rendered at all.
 * An empty `og:url`/`og:image` is worse than a missing one - crawlers resolve it
 * against the page url and end up scraping a non existing page.
 */
export const Meta: ComponentType<MetaProps> = ({
    title,
    description,
    image,
    imageWidth = shareImage.width,
    imageHeight = shareImage.height,
    imageType = shareImage.type,
    url
}) => {
    return (
        <Helmet>
            {title && <title key="meta-title">{title}</title>}
            {description && (
                <meta
                    key="meta-description"
                    name="description"
                    content={description}
                />
            )}
            {url && <link key="meta-canonical" rel="canonical" href={url} />}

            {url && <meta key="meta-og:url" property="og:url" content={url} />}
            {title && (
                <meta key="meta-og:title" property="og:title" content={title} />
            )}
            {description && (
                <meta
                    key="meta-og:description"
                    property="og:description"
                    content={description}
                />
            )}
            {image && (
                <meta key="meta-og:image" property="og:image" content={image} />
            )}
            {image && (
                <meta
                    key="meta-og:image:secure_url"
                    property="og:image:secure_url"
                    content={image}
                />
            )}
            {image && (
                <meta
                    key="meta-og:image:type"
                    property="og:image:type"
                    content={imageType}
                />
            )}
            {image && (
                <meta
                    key="meta-og:image:width"
                    property="og:image:width"
                    content={`${imageWidth}`}
                />
            )}
            {image && (
                <meta
                    key="meta-og:image:height"
                    property="og:image:height"
                    content={`${imageHeight}`}
                />
            )}
            {image && title && (
                <meta
                    key="meta-og:image:alt"
                    property="og:image:alt"
                    content={title}
                />
            )}

            {title && (
                <meta
                    key="meta-twitter:title"
                    name="twitter:title"
                    content={title}
                />
            )}
            {description && (
                <meta
                    key="meta-twitter:description"
                    name="twitter:description"
                    content={description}
                />
            )}
            {image && (
                <meta
                    key="meta-twitter:image"
                    name="twitter:image"
                    content={image}
                />
            )}
        </Helmet>
    )
}
